import { useQuery } from '@tanstack/react-query';
import type {
  BlogHeroProps,
  BlogNavigationProps,
} from '@we-make-websites/ui-lib';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { createContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import { useGetBlogLandingQuery } from 'lib/contentful/__generated__/BlogLandingQuery';
import type { GetBlogLandingQuery } from 'lib/contentful/__generated__/BlogLandingQuery';
import { contentfulQueryDataSource } from 'lib/contentful/dataSources';
import { formatContentfulSections } from 'utils/format/contentful/formatContentfulSections';
import { formatModuleBlogHero } from 'utils/format/contentful/formatModuleBlogHero';
import { formatModuleBlogNavigation } from 'utils/format/contentful/formatModuleBlogNavigation';
import { usePreviewCookie } from 'utils/getPreviewCookie';
import type {
  FormattedContentfulSection,
  InputContentfulSections,
} from 'utils/types/contentfulSections';
import type { StoreSeo } from 'utils/types/storeTypes';

type BlogLandingData = {
  seo?: StoreSeo;
  hero?: BlogHeroProps;
  navigation?: BlogNavigationProps;
  articlesListing?: FormattedContentfulSection[];
};

export type BlogLandingContext = BlogLandingData & {
  isLoading: boolean;
};

export const BlogLandingContext = createContext<BlogLandingContext | undefined>(
  undefined
);

export function BlogLandingProvider({ children }: { children: ReactNode }) {
  const { locale } = useRouter();

  const preview = usePreviewCookie();

  const variables = {
    handle: 'blogs',
    locale: storeLocale(locale),
    preview,
  };

  const { data, isLoading } = useQuery(
    useGetBlogLandingQuery.getKey(variables),
    useGetBlogLandingQuery.fetcher(
      contentfulQueryDataSource({ byPassCache: preview }),
      variables
    ),
    {
      select: getBlogLandingSelect,
    }
  );

  const contextValue = useMemo(() => {
    if (data) {
      return {
        ...data,
        isLoading,
      };
    }

    return {
      isLoading,
      title: '',
    };
  }, [data, isLoading]);

  return (
    <BlogLandingContext.Provider value={contextValue}>
      {children}
    </BlogLandingContext.Provider>
  );
}

const getBlogLandingSelect = (
  originalData: GetBlogLandingQuery
): BlogLandingData | null => {
  if (!originalData.blogs?.data[0]) {
    return null;
  }

  const blog = originalData.blogs?.data[0];
  const hero = blog.hero ? formatModuleBlogHero(blog.hero) : undefined;
  const navigation = blog.navigation
    ? formatModuleBlogNavigation(blog.navigation)
    : undefined;

  const articlesListingData =
    (blog.featuredArticlesCollection?.items as InputContentfulSections) ||
    ([] as InputContentfulSections);
  const articlesListing = articlesListingData.length
    ? formatContentfulSections(articlesListingData)
    : undefined;

  const seo = {
    title: blog.seoTitle || '',
    description: blog.seoDescription || '',
  };

  return {
    seo,
    hero,
    navigation,
    articlesListing,
  };
};
